import { Form } from "antd";
import { Tooltip } from "antd-v5";
import { BaseModalRef } from "components/BaseModal";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { InfoIcon } from "components/Icons";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { isEmpty } from "lodash";
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, nameRegexValidator } from "utils/formUtils";
import { duplicateNameValidator } from "utils/validatorUtil";

interface AWSIntegrationModalFormProps {
  configData: {
    id: number;
    mapAWSSecurityHub?: boolean;
    mapAmazonInspector?: boolean;
  };
  name: string;
  afterClose: () => void;
  configList: { name: string }[];
  onUpdateConfigComponent: () => void;
}
export default forwardRef<BaseModalRef, AWSIntegrationModalFormProps>(
  function AWSIntegrationModalForm(
    {
      configData,
      name = "AWS Infrastructure",
      afterClose,
      configList,
      onUpdateConfigComponent,
    },
    ref,
  ) {
    const featureFlags = useFeatureFlag();
    const [loginId, setLoginID] = useState(configData?.id);
    const [form] = Form.useForm();

    const addCreds = useCallback(async () => {
      const data = form.getFieldsValue();
      data.fetchAssets = true;
      const api = loginId
        ? ToolsIntegrationsAPI.updateGenericLoginDetail(data, name, loginId)
        : ToolsIntegrationsAPI.postGenericLoginDetail(data, name);
      const response = await api.promise;
      setLoginID(response.id);
      message.success(
        loginId
          ? "Credentials updated successfully"
          : "Credentials added successfully",
      );
      //invoke listing API on edit
      if (loginId) {
        onUpdateConfigComponent();
      }
    }, [form, name, loginId, onUpdateConfigComponent]);

    const isEditMode = !!configData?.id;
    const normalizeBoolean = (val: any) => val === "true" || val === true;
    const newConfig = {
      ...configData,
      mapAWSSecurityHub: isEditMode
        ? normalizeBoolean(configData.mapAWSSecurityHub)
        : true,
      mapAmazonInspector: isEditMode
        ? normalizeBoolean(configData.mapAmazonInspector)
        : false,
    };
    const fields = useMemo(
      () => [
        {
          name: "name",
          label: "Configuration Name",
          NoDefaultLengthRule: true,
          rules: [
            nameRegexValidator,
            duplicateNameValidator({ list: configList }),
          ],
          props: {
            autoFocus: true,
          },
          required: true,
        },
        {
          name: "roleARN",
          label: "Role ARN",
          required: true,
        },
        {
          name: "externalId",
          label: "External ID",
          required: true,
        },
        ...(featureFlags.RBVM_ASSETS
          ? [
              {
                type: FieldTypes.LABEL,
                value:
                  "Automate the creation of additional configurations with the AWS Infrastructure integration",
              },
              {
                name: "mapAWSSecurityHub",
                label: (
                  <>
                    Configure AWS Security Hub
                    <Tooltip title="Configure and ingests findings from AWS Security Hub across all available accounts and products available including Amazon GuardDuty and Inspector.">
                      <InfoIcon className="m-l-xs" />
                    </Tooltip>
                  </>
                ),
                type: FieldTypes.BOOLEAN,
                inline: true,
                disabled: isEditMode,
              },
              {
                name: "mapAmazonInspector",
                label: "Configure Amazon Inspector",
                type: FieldTypes.BOOLEAN,
                inline: true,
                disabled: isEditMode,
              },
            ]
          : []),
      ],
      [configList, featureFlags, isEditMode],
    );

    return (
      <BaseModalForm
        title={
          !isEmpty(configData)
            ? `Edit ${name} Configuration`
            : `Configure ${name}`
        }
        ref={ref}
        form={form}
        fields={fields}
        onOk={addCreds}
        initialValues={newConfig}
        afterClose={afterClose}
      />
    );
  },
);
